/*** IMPORTS FROM imports-loader ***/
import hydrator from "@gjensidige/views-api/src/runtime/hydrator";
import Component from ".";

/* global hydrator, Component */

// create a map of what we have hydrated, share it on window
window.__HYDRATED__ = window.__HYDRATED__ || {};

// get the current script executing
const partSrc = window.document?.currentScript?.getAttribute('src');

// filter the ones that need to be hydrated (data-nodeid contains the id for the props -> only hydration on node id's)
const parts = Array.from(
  document.querySelectorAll('script[src="' + partSrc + '"]')
).filter((part) => part.getAttribute('data-nodeid'));

// if this selection of parts contains elements that needs to be hydrated, add an eventListener
if (parts.length && window.addEventListener) {
  window.addEventListener('load', function () {
    parts.forEach((part) => {
      // make sure we only hydrate our components once
      const NODE_ID = part.getAttribute('data-nodeid');
      if (!window.__HYDRATED__[NODE_ID]) {
        window.__HYDRATED__[NODE_ID] = true;
        hydrator(Component, NODE_ID);
      }
    });
  });
}


import '@gjensidige/nci-core-grid/lib-css/index.css';

import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types/prop-types';
import { Title } from '@gjensidige/builders-components';
import { ArrowRight } from '@gjensidige/builders-icons';
import { Col } from '@gjensidige/nci-core-grid/lib-css/col';
import { Container } from '@gjensidige/nci-core-grid/lib-css/container';
import { Row } from '@gjensidige/nci-core-grid/lib-css/row';

import { CtaCampaignProps as Props } from './types';

import styles from '../less/cta-campaign.module.less';

export const CtaCampaign = ({
  title,
  text,
  target = '#',
  image,
  backgroundColor = 'yellow',
  alignment = 'left'
}: Props) => (
  <Container className={classNames(styles.ctaCampaign, styles.marginYParts)}>
    <Row>
      <Col md={12}>
        <a href={target} className={styles.link}>
          <Row>
            {image && (
              <Col md={12}>
                <img
                  src={image.url}
                  srcSet={image.srcset}
                  alt={image.alt ?? ''}
                  className={styles.photo}
                  width={16}
                  height={9}
                  sizes={
                    image.srcset &&
                    `(min-width: 1024px) and (max-width: 1199px) 1024px,
                    (min-width: 1200px) and (max-width: 1619px) 1200px,
                    (min-width: 1620px) 1620px,
                    100vw`
                  }
                  loading="lazy"
                />
              </Col>
            )}

            <Col sm={12} className={styles.mobileInfoBoxWrapper}>
              <MobileInfoBlock
                title={title}
                text={text}
                target={target}
                alignment={alignment}
                backgroundColor={backgroundColor}
              />
            </Col>
          </Row>
          <DesktopInfoBlock
            title={title}
            text={text}
            target={target}
            backgroundColor={backgroundColor}
            alignment={alignment}
          />
        </a>
      </Col>
    </Row>
  </Container>
);

CtaCampaign.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  alignment: PropTypes.string,
  image: PropTypes.any.isRequired
};

CtaCampaign.CSR = true;

export default CtaCampaign;

const AnimatedArrow = () => (
  <svg
    id="e4AbL9kqBJN1"
    xmlns="http://www.w3.org/2000/svg"
    width="60"
    height="32"
    viewBox="0 0 60 32"
    shapeRendering="geometricPrecision"
    textRendering="geometricPrecision"
  >
    <line
      x1="16"
      y1="16"
      x2="10"
      y2="16"
      fill="none"
      stroke="#090c33"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDashoffset={6}
      strokeDasharray={6}
      className={styles.arrowLineSmall}
    />
    <line
      x1="16"
      y1="16"
      x2="59"
      y2="16"
      fill="none"
      stroke="#090c33"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDashoffset={43}
      strokeDasharray={43}
      className={styles.arrowline}
    />
    <line
      x1="0"
      y1="0"
      x2="16"
      y2="16"
      fill="none"
      stroke="#090c33"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.arrowpoint}
    />
    <line
      x1="0"
      y1="32"
      x2="16"
      y2="16"
      fill="none"
      stroke="#090c33"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.arrowpoint}
    />
  </svg>
);

const MobileInfoBlock = ({
  title,
  text,
  backgroundColor = 'yellow'
}: Props) => {
  return (
    <Row
      className={classNames(
        styles.mobileInfoBox,
        styles[`${backgroundColor}Background`]
      )}
    >
      <Col sm={12} md={12}>
        {title && <Title size="4" className={styles.title} text={title} />}
        {text && (
          <div
            className={styles.preface}
            dangerouslySetInnerHTML={{ __html: text }}
          ></div>
        )}
      </Col>
      <Col md={1} xxs={12} className={styles.arrowCol}>
        <ArrowContainer />
      </Col>
    </Row>
  );
};

const DesktopInfoBlock = ({
  title,
  text,
  backgroundColor = 'yellow',
  alignment
}: Props) => {
  return (
    <Row className={styles.absoluteRow}>
      <Col xl={alignment === 'left' ? { span: 7 } : { offset: 5, span: 7 }}>
        <Row
          className={classNames(
            styles[`${backgroundColor}Background`],
            styles.desktopInfoBox
          )}
        >
          <Col sm={12} md={11}>
            {title && <Title size="4" className={styles.title} text={title} />}
            {text && (
              <div
                className={styles.preface}
                dangerouslySetInnerHTML={{ __html: text }}
              ></div>
            )}
          </Col>
          <Col md={1} sm={12} className={styles.arrowCol}>
            <ArrowContainer />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const ArrowContainer = () => {
  return (
    <>
      <div className={styles.animatedArrowWrapper}>
        <AnimatedArrow />
      </div>
      <div className={styles.arrowWrapper}>
        <ArrowRight />
      </div>
    </>
  );
};
